import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useEffect } from 'react';
import { useEquipment } from '../../contexts/EquipmentContext';
import ReviewEquipment from './ReviewEquipment';

// Reusable EditableField component
const EditableField = ({ label, value, onSave, fieldKey, savedChanges }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');
    const [hasChanges, setHasChanges] = useState(false);
    
    // Check if this field has saved changes
    const hasSavedChanges = savedChanges && savedChanges[fieldKey] !== undefined;

    const handleEdit = () => {
        setIsEditing(true);
        setEditValue(value || '');
    };

    const handleSave = () => {
        if (editValue !== value) {
            onSave(fieldKey, editValue);
            setHasChanges(true);
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value || '');
        setIsEditing(false);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="text-xs text-font-secondary font-medium">{label}</label>
                <button
                    onClick={handleEdit}
                    className="text-xs bg-bgSecondary button-container text-accent p-1.5 rounded hover:bg-accent hover:text-background transition-colors duration-300"
                >
                    <Icon icon="mdi:pencil" className="text-sm" />
                </button>
            </div>
            <p className="text-sm text-font-primary">{value || 'N/A'}</p>
            
            {isEditing && (
                <div className="space-y-2">
                    <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={handleSave}
                            className="text-xs bg-primary text-font-primary px-2 py-1 rounded hover:bg-primary/80 transition-colors duration-300"
                        >
                            Save
                        </button>
                        <button
                            onClick={handleCancel}
                            className="text-xs bg-bgSecondary text-font-primary px-2 py-1 rounded hover:bg-accent hover:text-background transition-colors duration-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            
            {(hasChanges && editValue !== value) || hasSavedChanges ? (
                <div className="text-xs text-green-600 bg-green-50 p-2 rounded border border-green-200 flex flex-row items-start gap-1">
                    <Icon icon="icon-park-outline:switch" className="text-green-600 text-lg" />
                    {hasSavedChanges ? savedChanges[fieldKey] : editValue}
                </div>
            ) : null}
        </div>
    );
};

const EditEquipment = ({ onBack, initialParentId, initialChildId }) => {
    const { equipmentData } = useEquipment();
    const [selectedParentId, setSelectedParentId] = useState('');
    const [selectedChildId, setSelectedChildId] = useState('');
    const [parentChanges, setParentChanges] = useState({});
    const [childChanges, setChildChanges] = useState({});
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    
    // Get the selected parent category object
    const selectedParent = equipmentData?.categories?.find(cat => cat.parentEqpId === selectedParentId);
    
    // Get child items for the selected parent
    const childItems = selectedParent?.childItems || [];
    
    // Get the selected child item object
    const selectedChild = childItems.find(child => child.childCategory === selectedChildId);

    // Set initial selections when component mounts or props change
    useEffect(() => {
        if (initialParentId) {
            setSelectedParentId(initialParentId);
        }
        if (initialChildId) {
            setSelectedChildId(initialChildId);
        }
    }, [initialParentId, initialChildId]);

    const handleParentSave = (fieldKey, value) => {
        setParentChanges(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleChildSave = (fieldKey, value) => {
        setChildChanges(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleReview = () => {
        setIsReviewMode(true);
    };

    const handleBackToEdit = () => {
        setIsReviewMode(false);
    };

    const handleExitAttempt = () => {
        if (Object.keys(parentChanges).length > 0 || Object.keys(childChanges).length > 0) {
            setShowExitConfirm(true);
        } else {
            onBack();
        }
    };

    const handleConfirmExit = () => {
        setShowExitConfirm(false);
        onBack();
    };

    const handleCancelExit = () => {
        setShowExitConfirm(false);
    };

    // If in review mode, show the ReviewEquipment component
    if (isReviewMode) {
        return (
            <ReviewEquipment
                onBack={handleBackToEdit}
                onSuccess={onBack}
                parentChanges={parentChanges}
                childChanges={childChanges}
                selectedParent={selectedParent}
                selectedChild={selectedChild}
            />
        );
    }

    return (
        <div className="responsive-page-layout scrollable">
            {/* Header */}
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-start text-2xl">
                    <Icon icon="fluent:box-edit-20-regular" className="text-primary text-3xl"></Icon>
                    Edit Equipment
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={handleExitAttempt}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-primary" />
                        <span className="text-sm font-medium group-hover:text-font-primary">Back to Inventory</span>
                    </button>
                    
                    {/* Review button - only shows when there are changes */}
                    {(Object.keys(parentChanges).length > 0 || Object.keys(childChanges).length > 0) && (
                        <button 
                            onClick={handleReview}
                            className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-accent hover:text-background transition-colors duration-300"
                        >
                            <Icon icon="mdi:eye" className="text-xl text-accent group-hover:text-background" />
                            <span className="text-sm font-medium group-hover:text-background">Review</span>
                        </button>
                    )}
                </div>
            </div>
            
            {/* Dropdown Selectors */}
            <div className="edit-equipment-selectors-responsive flex items-center justify-between w-full gap-4">
                <div className="flex flex-col items-start gap-2 w-full lg:w-1/2">
                    <div className="flex flex-row items-center gap-1">
                        <Icon icon="duo-icons:box-2" className="text-2xl text-primary" />
                        <label htmlFor="select-category" className="text-sm text-font-primary font-semibold whitespace-nowrap">Select Parent</label>
                    </div>
                    <select 
                        id="select-category" 
                        value={selectedParentId}
                        onChange={(e) => {
                            setSelectedParentId(e.target.value);
                            setSelectedChildId(''); // Reset child selection when parent changes
                        }}
                        className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none"
                    >
                        <option value="">Select a category</option>
                        {equipmentData?.categories?.map((category) => (
                            <option key={category.parentEqpId} value={category.parentEqpId}>
                                {category.parentCategory || 'Unnamed Category'}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col items-start gap-2 w-full lg:w-1/2">
                    <div className="flex flex-row items-center gap-1">
                        <Icon icon="ix:box-open" className={`text-2xl ${selectedChildId ? 'text-primary' : 'text-font-secondary'}`} />
                        <label htmlFor="select-child" className={`text-sm text-font-primary font-semibold whitespace-nowrap ${selectedChildId ? 'text-accent' : 'text-font-secondary'}`}>Select Child</label>
                    </div>
                    <select 
                        id="select-child" 
                        value={selectedChildId}
                        onChange={(e) => setSelectedChildId(e.target.value)}
                        disabled={!selectedParentId}
                        className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <option value="">Select a child category</option>
                        {childItems.map((child, childIndex) => (
                            <option key={child.childEqpId || child.childCategory || `child_option_${childIndex}`} value={child.childCategory}>
                                {child.childCategory || 'Unnamed Item'}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Fields Container */}
            {!selectedParent ? (
                // Empty state when no parent is selected
                <div className="flex flex-col items-center justify-center h-full w-full py-16">
                    <Icon icon="fluent:box-edit-20-regular" className="text-8xl text-font-secondary mb-4" />
                    <p className="text-2xl text-font-secondary font-medium">Select a Parent Category</p>
                </div>
            ) : (
                <div className="edit-equipment-fields-responsive flex w-full gap-4">
                    {/* Parent Category Fields */}
                    {selectedParent && (
                        <div className={`flex flex-col items-start gap-2 w-full ${selectedChildId ? 'lg:w-1/2' : 'lg:w-full'}`}>
                            <div className="flex flex-row items-center gap-1 justify-between w-full">
                                <div className="flex flex-row items-center gap-1">
                                    <Icon icon="duo-icons:box-2" className="text-2xl text-accent" />
                                    <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Parent Details</label>
                                </div>
                                <div className="flex flex-row items-center gap-1">
                                    <label className="text-xs text-font-secondary font-medium">Parent ID:</label>
                                    <p className="text-sm text-font-primary font-mono font-medium">{selectedParent.parentEqpId || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                                <EditableField
                                    label="Category Name"
                                    value={selectedParent.parentCategory}
                                    onSave={handleParentSave}
                                    fieldKey="parentCategory"
                                    savedChanges={parentChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Typical Uses"
                                    value={selectedParent.typicalUses}
                                    onSave={handleParentSave}
                                    fieldKey="typicalUses"
                                    savedChanges={parentChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Typical Flow"
                                    value={selectedParent.typicalFlow}
                                    onSave={handleParentSave}
                                    fieldKey="typicalFlow"
                                    savedChanges={parentChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Defaults"
                                    value={selectedParent.defaults}
                                    onSave={handleParentSave}
                                    fieldKey="defaults"
                                    savedChanges={parentChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Qualifying Questions"
                                    value={selectedParent.qualifyingQuestions}
                                    onSave={handleParentSave}
                                    fieldKey="qualifyingQuestions"
                                    savedChanges={parentChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Special Notes"
                                    value={selectedParent.specialNotes}
                                    onSave={handleParentSave}
                                    fieldKey="specialNotes"
                                    savedChanges={parentChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Pricing Considerations"
                                    value={selectedParent.pricingConsiderations}
                                    onSave={handleParentSave}
                                    fieldKey="pricingConsiderations"
                                    savedChanges={parentChanges}
                                />
                            </div>
                        </div>
                    )}

                    {/* Child Item Fields */}
                    {selectedChildId && selectedParent && selectedChild && (
                        <div className="w-full lg:w-1/2 flex flex-col items-start gap-3">
                            <div className="flex flex-row items-center gap-1 justify-between w-full">
                                <div className="flex flex-row items-center gap-1">
                                    <Icon icon="ix:box-open" className="text-2xl text-accent" />
                                    <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Child Details</label>
                                </div>
                                <div className="flex flex-row items-center gap-1">
                                    <label className="text-xs text-font-secondary font-medium">Child ID:</label>
                                    <p className="text-sm text-font-primary font-mono font-medium">{selectedChild.childEqpId || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="w-full rounded-lg p-2 flex flex-col gap-2">
                                <EditableField
                                    label="Item Name"
                                    value={selectedChild.childCategory}
                                    onSave={handleChildSave}
                                    fieldKey="childCategory"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Parent Category"
                                    value={selectedParent.parentCategory}
                                    onSave={handleChildSave}
                                    fieldKey="parentCategory"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Model"
                                    value={selectedChild.model}
                                    onSave={handleChildSave}
                                    fieldKey="model"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Customer Reference"
                                    value={selectedChild.customerReference}
                                    onSave={handleChildSave}
                                    fieldKey="customerReference"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Brand"
                                    value={selectedChild.brand}
                                    onSave={handleChildSave}
                                    fieldKey="brand"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Common Synonyms"
                                    value={selectedChild.commonSynonyms}
                                    onSave={handleChildSave}
                                    fieldKey="commonSynonyms"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Key Selling Points"
                                    value={selectedChild.keySellingPoints}
                                    onSave={handleChildSave}
                                    fieldKey="keySellingPoints"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Description"
                                    value={selectedChild.description}
                                    onSave={handleChildSave}
                                    fieldKey="description"
                                    savedChanges={childChanges}
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <EditableField
                                    label="Pricing"
                                    value={selectedChild.pricing}
                                    onSave={handleChildSave}
                                    fieldKey="pricing"
                                    savedChanges={childChanges}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Exit Confirmation Dialog */}
            {showExitConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <Icon icon="mdi:alert-circle" className="text-accent text-2xl" />
                            <h3 className="text-lg font-semibold text-font-primary">Unsaved Changes</h3>
                        </div>
                        <p className="text-font-secondary mb-6">
                            You have unsaved changes. Are you sure you want to exit? All changes will be lost.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleCancelExit}
                                className="px-4 py-2 text-font-primary bg-bgSecondary rounded-lg hover:bg-accent hover:text-background transition-colors duration-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmExit}
                                className="px-4 py-2 bg-accent text-font-primary hover:bg-background hover:text-accent rounded-lg hover:bg-accent/80 transition-colors duration-300"
                            >
                                Exit Without Saving
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditEquipment;
